import { useEffect, useMemo, useState, type ReactNode } from "react";
import { Topbar } from "@/components/parcelio/topbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { trackersApi, vehiclesApi, type Tracker, type Vehicle } from "@/lib/api";
import { useAuth } from "@/stores/auth";
import { Plus, Search, Pencil, Trash2, Satellite, Truck, Check, ChevronsUpDown, Link2Off, Eye, TriangleAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { LoadingBlock } from "@/components/ui/spinner";
import { BatteryBadge, SignalBadge, isLowBattery, isStaleCheckin, STALE_CHECKIN_HOURS } from "@/components/parcelio/device-health";

const MANUFACTURERS = ["Concox", "Coban", "Teltonika", "Queclink", "Meitrack", "Ruptela", "Other"];
const PROTOCOLS = [
  { value: "gt06", label: "GT06 (raw TCP)" },
  { value: "gps303", label: "GPS303 / TK303 (raw TCP)" },
  { value: "h02", label: "H02 (raw TCP)" },
  { value: "teltonika", label: "Teltonika Codec8 (raw TCP)" },
  { value: "meitrack", label: "Meitrack (raw TCP)" },
  { value: "generic_http", label: "Generic HTTP/JSON" },
];

type Form = {
  device_name: string;
  manufacturer: string;
  protocol: string;
  imei: string;
  phone_number: string;
  device_password: string;
};
const empty: Form = { device_name: "", manufacturer: MANUFACTURERS[0], protocol: "gt06", imei: "", phone_number: "", device_password: "" };

function VehiclePicker({ value, onChange, vehicles }: { value: string; onChange: (v: string) => void; vehicles: Vehicle[] }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const filtered = vehicles.filter((v) => v.plate.toLowerCase().includes(query.toLowerCase()) || (v.make ?? "").toLowerCase().includes(query.toLowerCase()));
  const selected = vehicles.find((v) => v.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal">
          {selected ? `${selected.plate} — ${selected.make} ${selected.model}` : "Select vehicle"}
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search plate…" value={query} onValueChange={setQuery} />
          <CommandList>
            <CommandEmpty>No vehicle found.</CommandEmpty>
            <CommandGroup>
              {filtered.map((v) => (
                <CommandItem key={v.id} value={v.id} onSelect={() => { onChange(v.id); setOpen(false); }}>
                  <Check className={cn("h-4 w-4", value === v.id ? "opacity-100" : "opacity-0")} />
                  {v.plate} — {v.make} {v.model}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-0.5">{value}</div>
    </div>
  );
}

export default function Trackers() {
  const token = useAuth((s) => s.session?.accessToken);
  const [items, setItems] = useState<Tracker[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Tracker | null>(null);
  const [form, setForm] = useState<Form>(empty);
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState<Tracker | null>(null);
  const [alertsOpen, setAlertsOpen] = useState(false);

  const [assignFor, setAssignFor] = useState<Tracker | null>(null);
  const [assignVehicle, setAssignVehicle] = useState("");
  const [toDelete, setToDelete] = useState<Tracker | null>(null);

  const load = () => {
    setLoading(true);
    Promise.all([trackersApi.list(token), vehiclesApi.list(token)])
      .then(([t, v]) => {
        setItems(t);
        setVehicles(v);
      })
      .catch(() => toast.error("Failed to load trackers"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const filtered = useMemo(() => items.filter((t) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      t.device_name.toLowerCase().includes(s) ||
      t.imei.includes(s) ||
      t.manufacturer.toLowerCase().includes(s) ||
      (t.vehicle_plate?.toLowerCase().includes(s) ?? false)
    );
  }), [items, q]);

  const counts = {
    total: items.length,
    assigned: items.filter((t) => t.vehicle_id).length,
    unassigned: items.filter((t) => !t.vehicle_id).length,
  };

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (t: Tracker) => {
    setEditing(t);
    setForm({
      device_name: t.device_name,
      manufacturer: t.manufacturer,
      protocol: t.protocol,
      imei: t.imei,
      phone_number: t.phone_number ?? "",
      device_password: t.device_password ?? "",
    });
    setViewing(null);
    setOpen(true);
  };

  const alerts = useMemo(
    () => items.filter((t) => t.vehicle_id && (isLowBattery(t.last_battery_level) || isStaleCheckin(t.last_status_at))),
    [items],
  );

  const save = async () => {
    if (!form.device_name.trim() || !form.imei.trim()) {
      return toast.error("Device name and IMEI required");
    }
    setSaving(true);
    try {
      if (editing) {
        const updated = await trackersApi.update(editing.id, {
          device_name: form.device_name,
          manufacturer: form.manufacturer,
          protocol: form.protocol,
          imei: form.imei,
          phone_number: form.phone_number || undefined,
          device_password: form.device_password || undefined,
        } as Partial<Tracker>, token);
        setItems((cur) => cur.map((t) => (t.id === editing.id ? updated : t)));
        toast.success("Tracker updated");
      } else {
        const created = await trackersApi.create(
          {
            device_name: form.device_name,
            manufacturer: form.manufacturer,
            protocol: form.protocol,
            imei: form.imei,
            phone_number: form.phone_number || undefined,
            device_password: form.device_password || undefined,
          },
          token,
        );
        setItems((cur) => [created, ...cur]);
        toast.success("Tracker added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save tracker");
    } finally {
      setSaving(false);
    }
  };

  const openAssign = (t: Tracker) => {
    setAssignFor(t);
    setAssignVehicle(t.vehicle_id ?? "");
  };

  const confirmAssign = async () => {
    if (!assignFor || !assignVehicle) return;
    try {
      const updated = await trackersApi.assign(assignFor.id, assignVehicle, token);
      setItems((cur) => cur.map((t) => {
        if (t.id === updated.id) return updated;
        // any other tracker the server bumped off this vehicle
        if (t.vehicle_id === updated.vehicle_id) return { ...t, vehicle_id: null, vehicle_plate: null };
        return t;
      }));
      toast.success(`Assigned to ${updated.vehicle_plate ?? "vehicle"}`);
      setAssignFor(null);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not assign tracker");
    }
  };

  const unassign = async (t: Tracker) => {
    try {
      const updated = await trackersApi.unassign(t.id, token);
      setItems((cur) => cur.map((x) => (x.id === t.id ? updated : x)));
      toast.success("Tracker unassigned");
    } catch {
      toast.error("Could not unassign tracker");
    }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await trackersApi.remove(toDelete.id, token);
      setItems((cur) => cur.filter((t) => t.id !== toDelete.id));
      toast.success("Tracker removed");
    } catch {
      toast.error("Could not remove tracker");
    } finally {
      setToDelete(null);
    }
  };

  return (
    <>
      <Topbar
        title="Trackers"
        subtitle={`${counts.total} devices · ${counts.assigned} assigned · ${counts.unassigned} unassigned`}
        actions={
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="outline"
              className={alerts.length > 0 ? "border-destructive/30 text-destructive hover:text-destructive" : undefined}
              onClick={() => setAlertsOpen(true)}
            >
              <TriangleAlert className="mr-1.5 h-4 w-4" /> Alerts{alerts.length > 0 ? ` (${alerts.length})` : ""}
            </Button>
            <Button size="sm" onClick={openCreate}><Plus className="mr-1.5 h-4 w-4" /> Add tracker</Button>
          </div>
        }
      />
      <div className="flex-1 space-y-4 p-4 md:p-6">
        <div className="grid gap-3 sm:grid-cols-3">
          {(["total", "assigned", "unassigned"] as const).map((k) => (
            <div key={k} className="surface-card p-4">
              <div className="text-xs uppercase tracking-wider text-muted-foreground capitalize">{k}</div>
              <div className="mt-1 text-2xl font-bold">{counts[k]}</div>
            </div>
          ))}
        </div>

        <div className="surface-card">
          <div className="flex flex-col gap-3 border-b p-4 md:flex-row md:items-center md:justify-between">
            <div className="relative max-w-sm flex-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search device, IMEI, plate…" className="pl-9" />
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Device</TableHead>
                <TableHead>Manufacturer</TableHead>
                <TableHead>Protocol</TableHead>
                <TableHead>IMEI</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7}><LoadingBlock label="Loading trackers…" /></TableCell></TableRow>
              ) : (
                <>
                  {filtered.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell>
                        <div className="flex items-center gap-2 font-medium">
                          <Satellite className="h-3.5 w-3.5 text-muted-foreground" /> {t.device_name}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{t.manufacturer}</TableCell>
                      <TableCell className="text-xs uppercase text-muted-foreground">{t.protocol}</TableCell>
                      <TableCell className="font-mono text-xs">{t.imei}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{t.phone_number ?? "—"}</TableCell>
                      <TableCell>
                        {t.vehicle_id ? (
                          <span className="inline-flex items-center gap-1 rounded-full border border-success/20 bg-success/10 px-2 py-0.5 text-[11px] font-medium text-success">
                            <Truck className="h-3 w-3" /> {t.vehicle_plate}
                          </span>
                        ) : (
                          <span className="inline-flex rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                            Unassigned
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => openAssign(t)} aria-label="Assign to vehicle"><Truck className="h-3.5 w-3.5" /></Button>
                        {t.vehicle_id ? (
                          <Button variant="ghost" size="icon" onClick={() => unassign(t)} aria-label="Unassign"><Link2Off className="h-3.5 w-3.5" /></Button>
                        ) : null}
                        <Button variant="ghost" size="icon" onClick={() => setViewing(t)} aria-label="View"><Eye className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => setToDelete(t)} aria-label="Delete"><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="py-6 text-center text-sm text-muted-foreground">No trackers match.</TableCell></TableRow>
                  ) : null}
                </>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.device_name}` : "Add tracker"}</DialogTitle>
            <DialogDescription>Hardware GPS tracker inventory — IMEI must match the device's real identifier.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div><Label>Device name</Label><Input value={form.device_name} onChange={(e) => setForm({ ...form, device_name: e.target.value })} /></div>
            <div>
              <Label>Manufacturer</Label>
              <Select value={form.manufacturer} onValueChange={(v) => setForm({ ...form, manufacturer: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {MANUFACTURERS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Protocol</Label>
              <Select value={form.protocol} onValueChange={(v) => setForm({ ...form, protocol: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROTOCOLS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>IMEI</Label><Input value={form.imei} onChange={(e) => setForm({ ...form, imei: e.target.value })} placeholder="356938035643809" /></div>
            <div><Label>Phone number</Label><Input value={form.phone_number} onChange={(e) => setForm({ ...form, phone_number: e.target.value })} placeholder="SIM number, for SMS config" /></div>
            <div><Label>Device password</Label><Input value={form.device_password} onChange={(e) => setForm({ ...form, device_password: e.target.value })} placeholder="SMS admin password (default often 123456)" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{editing ? "Save" : "Add"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignFor} onOpenChange={(v) => !v && setAssignFor(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign {assignFor?.device_name}</DialogTitle>
            <DialogDescription>
              If the target vehicle already has a tracker, it's automatically unassigned. If this tracker
              was on another vehicle, that vehicle loses its device link.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <VehiclePicker value={assignVehicle} onChange={setAssignVehicle} vehicles={vehicles} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignFor(null)}>Cancel</Button>
            <Button onClick={confirmAssign} disabled={!assignVehicle}>Assign</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Satellite className="h-4 w-4" /> {viewing?.device_name}</DialogTitle>
            <DialogDescription>Device inventory + latest known health.</DialogDescription>
          </DialogHeader>
          {viewing ? (
            <div className="grid grid-cols-2 gap-3 py-2 text-sm">
              <Field label="Manufacturer" value={viewing.manufacturer} />
              <Field label="Protocol" value={<span className="uppercase">{viewing.protocol}</span>} />
              <Field label="IMEI" value={<span className="font-mono text-xs">{viewing.imei}</span>} />
              <Field label="Phone number" value={viewing.phone_number ?? "—"} />
              <Field
                label="Vehicle"
                value={viewing.vehicle_plate ?? <span className="text-muted-foreground">Unassigned</span>}
              />
              <Field label="Battery" value={<BatteryBadge level={viewing.last_battery_level} />} />
              <Field label="Signal" value={<SignalBadge level={viewing.last_gsm_signal} />} />
              <Field
                label="Last checkin"
                value={viewing.last_status_at ? new Date(viewing.last_status_at).toLocaleString() : "—"}
              />
              {!viewing.vehicle_id ? (
                <p className="col-span-2 text-xs text-muted-foreground">
                  Battery/signal/checkin only report once this tracker is assigned to a vehicle.
                </p>
              ) : null}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewing(null)}>Close</Button>
            <Button onClick={() => viewing && openEdit(viewing)}>
              <Pencil className="mr-1.5 h-4 w-4" /> Edit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={alertsOpen} onOpenChange={setAlertsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><TriangleAlert className="h-4 w-4 text-destructive" /> Device alerts</DialogTitle>
            <DialogDescription>
              Assigned trackers with low battery (≤{1}/6) or no checkin in {STALE_CHECKIN_HOURS}h+.
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto py-1">
            {alerts.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted-foreground">No alerts — all assigned trackers look healthy.</p>
            ) : (
              <div className="space-y-2">
                {alerts.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className="flex w-full items-center justify-between rounded-md border p-3 text-left hover:bg-muted"
                    onClick={() => { setAlertsOpen(false); setViewing(t); }}
                  >
                    <div>
                      <div className="font-medium">{t.device_name}</div>
                      <div className="text-xs text-muted-foreground">{t.vehicle_plate}</div>
                    </div>
                    <div className="flex items-center gap-3">
                      {isLowBattery(t.last_battery_level) ? <BatteryBadge level={t.last_battery_level} /> : null}
                      {isStaleCheckin(t.last_status_at) ? (
                        <span className="text-xs text-destructive">
                          {t.last_status_at ? `Checked in ${new Date(t.last_status_at).toLocaleString()}` : "Never checked in"}
                        </span>
                      ) : null}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAlertsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {toDelete?.device_name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the tracker record and unlinks it from its vehicle. GPS pings from this IMEI will stop resolving.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
